import React, { useState } from 'react';

import APFormGroup from 'components/FormFields/APFormGroup';
import FormSelect from 'components/FormFields/FormSelect';
import InputField from 'components/FormFields/InputField';
import ImageUploadField from 'components/FormFields/ImageUploadField';
import FormCheckbox from 'components/FormFields/FormCheckbox';

import { MAX_NOTE_NAME_LENGTH, NOTE_RESTRICTIONS } from 'utils/constants';
import { useSelector } from 'react-redux';
import clsx from 'clsx';

const CreateNoteSettings = ({ type, weddingId, templateFolders, templates, createNote, closeNoteModal }) => {
  const [noteTitle, setNoteTitle] = useState('');
  const [restriction, setRestriction] = useState('None');

  const [imageKey, setImageKey] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [originalImageKey, setOriginalImageKey] = useState(null);

  const [shortcutUrl, setShortcutUrl] = useState('');
  const [isShortcutUrlValid, setShortcutUrlValid] = useState(true);

  const [noteTemplatesList, setNoteTemplatesList] = useState([]);
  const [noteTemplatesListName, setNoteTemplatesListName] = useState('Select...');

  const [error, setError] = useState('');
  const [isFormValid, setFormValid] = useState(true);

  const userInfo = useSelector((state) => state.global.userInfo);

  const handleNoteCreateValidation = () => {
    if ((type === 0 && (!shortcutUrl || !noteTitle))
      || (type === 1 && !noteTitle) || (type === 2 && !noteTemplatesList)) {
      setError('Please complete all required fields above.');
      setFormValid(false);
      return false;
    }
    /* Removing url validation for now. May want to add it back later.
    // This pattern failed validation https://url.com/subpage?page=argument#/collaboration/uuid
    if (type === 0 && !isUrlValid(shortcutUrl)) {
      setError('Please review and update your website format to continue.');
      setShortcutUrlValid(false);
      return false;
    }
    */
    setError('');
    setFormValid(true);
    return true;
  };

  const handleNoteCreate = async (e) => {
    e.currentTarget.disabled = true;
    if (handleNoteCreateValidation()) {
      let noteBody;
      if (type === 2) {
        noteBody = {
          templatesId: noteTemplatesList,
          weddingId,
        };
      } else {
        noteBody = {
          weddingId,
          title: noteTitle,
          plannerOnly: (restriction === 'VisibleToOrgMembersOnly'),
          clientReadOnly: restriction === ('ClientEditingLocked'),
          imageKey,
          url: shortcutUrl,
        };
      };
      createNote(noteBody);
      if (shortcutUrl || type === 2) closeNoteModal();
    } else {
      e.currentTarget.disabled = false;
    }
  };

  const renderNoteTitle = () => {
    return (
      <InputField
        name="note_title"
        label="Title"
        value={noteTitle}
        onChange={(event) => {
          setNoteTitle(event.target.value);
          setError('');
        }}
        showError={!isFormValid && !noteTitle}
        maxLength={MAX_NOTE_NAME_LENGTH}
        required
      />
    );
  };

  const renderShortcut = () => {
    return (
      <InputField
        name="shortcut_url"
        label="Url"
        placeholder="Enter a URL here. For Google and other applications, please use “share” links."
        value={shortcutUrl}
        onChange={(event) => {
          setShortcutUrl(event.target.value);
          setShortcutUrlValid(true);
          setFormValid(true);
          setError('');
        }}
        onBlur={() => {
          if (
            shortcutUrl &&
            !shortcutUrl.includes('https://')
          ) {
            setShortcutUrl(`https://${shortcutUrl}`);
          }
        }}
        showError={!isFormValid && (!shortcutUrl) || !isShortcutUrlValid}
        required
      />
    );
  };

  const renderRestrictions = (noteType) => {
    return (
      <FormSelect
        id="restrictions"
        name="Restrictions"
        defaultValue="None"
        value={restriction}
        onChange={(e) => {
          setRestriction(e.target.value);
          if (e.target.value === 'ClientEditingLocked' && noteType === 0) {
            setError(<>Heads up! Although client editing is locked for this shortcut in Aisle Planner, you may
              <br /> still need to manage outside permissions separately.</>);
          } else {
            setError('');
          }
        }}
      >
        {NOTE_RESTRICTIONS.map((option, index) => (
          <option value={option.value}
            key={`option_${index}`}
          >
            {option.label}
          </option>
        ))}
      </FormSelect>
    );
  };

  const renderCoverImage = () => {
    return (
      <ImageUploadField
        label='Cover image'
        imageUploadType='shortcut'
        imageUrl={imageUrl}
        originalImageKey={originalImageKey}
        updateImageKey={setImageKey}
        updateImageUrl={setImageUrl}
        updateOriginalImageKey={setOriginalImageKey}
        imageInfoPath='wedding/note/images'
        imageProcessPath='wedding/note/image'
        imageCropperUploadText='Drag your image here'
      />
    );
  };

  const handleClick = (e, id) => {
    let newNoteTemplatesListName = '';
    const newItems = [];
    templates.forEach(x => {
      if(x.value === id)
      {
        if(e.target.checked)
        {
          if(newNoteTemplatesListName === ''){
            newNoteTemplatesListName = x.label;
          } else {
            newNoteTemplatesListName = `${newNoteTemplatesListName}, ${x.label}`;
          };
          newItems.push(x.value);
        }
      } else if(noteTemplatesList.indexOf(x.value) !== -1)
      {
        if(newNoteTemplatesListName === ''){
          newNoteTemplatesListName = x.label;
        } else {
          newNoteTemplatesListName = `${newNoteTemplatesListName}, ${x.label}`;
        };
        newItems.push(x.value);
      }
    });
    setNoteTemplatesList(newItems);
    setNoteTemplatesListName(newNoteTemplatesListName);
  };

  const renderTemplate = () => {
    return (
      <FormSelect
        dropdownButtonProps={{
          className: 'note-template-select',
        }}
        name="Template"
        required
        fitModal
        responsive={false}
        emptyValue={noteTemplatesListName !== '' ? noteTemplatesListName : 'Select...'}
      >
        {templates.length ? (
          templateFolders.map((option, index) => (
            <optgroup label={option.label}
              key={`optgroup_${index}`}
            >
              {templates.filter((template) => template.folderId === option.value).map(
                (optionTemplate) => (
                  <FormCheckbox
                    name={optionTemplate.label}
                    onClick={e => handleClick(e, optionTemplate.value)}
                    checked={noteTemplatesList.indexOf(optionTemplate.value) !== -1}
                    className='note-template-select-checkbox'
                    wrapperProps={{
                      className: 'note-template-select-wrapper small-checkbox',
                    }}
                    labelProps={{
                      className: 'note-template-select-label',
                    }}
                  >
                    {optionTemplate.label}
                  </FormCheckbox>
                ),
              )}
            </optgroup>
          ))).filter((option) => option.props.children.length) : (
          [<option value="goto_settings">
            Did you know that you can create note templates for your most commonly used notes?
            Head on over to
            your <a href={`/app/pro/${userInfo.orgId}/settings/templates/notes-templates`}>templates section</a> to
            create your first Note Template.
          </option>]
        )}
      </FormSelect>
    );
  };

  const actionText = (noteType) => {
    if (noteType === 0) {
      return ('Create shortcut');
    }
    return ('Create note');
  };

  const createNoteContent = (noteType) => {
    if (noteType === 0) {
      return (
        <>
          {renderNoteTitle()}
          {renderShortcut()}
          {renderRestrictions(noteType)}
          {renderCoverImage()}
        </>
      );
    }
    if (noteType === 1) {
      return (
        <>
          {renderNoteTitle()}
          {renderRestrictions(noteType)}
          {renderCoverImage()}
        </>
      );
    }
    if (noteType === 2) {
      return (
        <>
          {renderTemplate()}
        </>
      );
    }
  };

  return (
    <>
      <APFormGroup grid={false}>
        {createNoteContent(type)}
      </APFormGroup>
      <p className="note-error mb-3">
        {error}
      </p>
      <button
        className={clsx('button', type === 2 && 'create-note-modal__button')}
        onClick={handleNoteCreate}
      >
        {actionText(type)}
      </button>
    </>
  );
};

export default CreateNoteSettings;
